import { apiFetch } from "./client.ts";

export interface Page<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

function qs(params: ListDocumentsParams): string {
  const s = new URLSearchParams();
  if (params.scope) s.set("scope", params.scope);
  if (params.status) s.set("status", params.status);
  if (params.classification) s.set("classification", params.classification);
  if (params.search) s.set("search", params.search);
  if (params.limit != null) s.set("limit", String(params.limit));
  if (params.offset != null) s.set("offset", String(params.offset));
  const r = s.toString();
  return r ? `?${r}` : "";
}

export interface KnowledgeDocument {
  id: string;
  title: string;
  scope: string;
  status: "PENDING" | "PROCESSING" | "READY" | "FAILED" | "ARCHIVED";
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
  classification: string;
  ownerId: string;
  clientId: string | null;
  projectId: string | null;
  tags: string[];
  version: number;
  chunkCount: number;
  embeddingCount: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SearchResult {
  chunkId: string;
  documentId: string;
  documentTitle: string;
  content: string;
  score: number;
  heading: string | null;
}

export interface SearchResponse {
  results: SearchResult[];
  totalFound: number;
  durationMs: number;
}

export interface KnowledgeOverview {
  documents: {
    total: number;
    ready: number;
    processing: number;
    pending: number;
    failed: number;
  };
  searches: number;
}

export interface ListDocumentsParams {
  scope?: string;
  status?: string;
  classification?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export async function getKnowledgeOverview(token: string): Promise<KnowledgeOverview> {
  return apiFetch<KnowledgeOverview>("/api/v1/knowledge/analytics/overview", { token });
}

export async function listDocuments(token: string, params: ListDocumentsParams = {}): Promise<Page<KnowledgeDocument>> {
  return apiFetch<Page<KnowledgeDocument>>(`/api/v1/knowledge/documents${qs(params)}`, { token });
}

export async function getDocument(token: string, id: string): Promise<KnowledgeDocument> {
  return apiFetch<KnowledgeDocument>(`/api/v1/knowledge/documents/${id}`, { token });
}

export async function uploadDocument(
  token: string,
  data: {
    title: string;
    scope: string;
    mimeType: string;
    sizeBytes: number;
    storageKey: string;
    classification?: string;
    tags?: string[];
  },
): Promise<KnowledgeDocument> {
  return apiFetch<KnowledgeDocument>("/api/v1/knowledge/documents", {
    token,
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function reindexDocument(token: string, id: string): Promise<KnowledgeDocument> {
  return apiFetch<KnowledgeDocument>(`/api/v1/knowledge/documents/${id}/reindex`, {
    token,
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function archiveDocument(token: string, id: string, version: number): Promise<void> {
  await apiFetch(`/api/v1/knowledge/documents/${id}`, {
    token,
    method: "DELETE",
    body: JSON.stringify({ version }),
  });
}

export async function searchKnowledge(
  token: string,
  query: string,
  options: { topK?: number; minScore?: number; scope?: string } = {},
): Promise<SearchResponse> {
  return apiFetch<SearchResponse>("/api/v1/knowledge/search", {
    token,
    method: "POST",
    body: JSON.stringify({
      query,
      topK: options.topK ?? 10,
      minScore: options.minScore ?? 0.5,
      filter: options.scope ? { scope: options.scope } : undefined,
    }),
  });
}
